import { formatCurrency } from '../../../app/currency';
import { getResolvedSavedSimulation } from '../../../extensions/price-simulator-3d/priceSimulatorMetadata';

export const VARIANT_WARN_AT = 50;
export const VARIANT_CONFIRM_AT = 100;
export const VARIANT_HARD_MAX = 500;
export const VARIANT_EXPAND_ALL_ACTIVE_MAX = 10;
export const MAX_OPTIONS = 3;
export const LEGACY_VARIANTS_PREVIEW_MAX = 20;

export function countActiveVariants(variants = []) {
  return variants.filter(
    (v) => !v.is_deleted && (v.status || 'ACTIVE') === 'ACTIVE',
  ).length;
}

export function getVariantAttributeValue(variant, option) {
  if (!variant || !option) return '';
  const attrs = variant.attributes || {};
  return String(attrs[option.key] ?? attrs[option.name] ?? '').trim();
}

export function getVariantCompleteness(variant, options = []) {
  const variationOptions = (options || []).filter((opt) => opt?.key || opt?.name);
  if (!variationOptions.length) {
    return { complete: true, missingKeys: [], invalidKeys: [] };
  }

  const missingKeys = [];
  const invalidKeys = [];
  variationOptions.forEach((opt) => {
    const key = opt.key || opt.name;
    const value = getVariantAttributeValue(variant, opt);
    if (!value) {
      missingKeys.push(key);
      return;
    }
    const allowed = opt.values || [];
    if (allowed.length > 0 && !allowed.includes(value)) {
      invalidKeys.push(key);
    }
  });

  return {
    complete: missingKeys.length === 0 && invalidKeys.length === 0,
    missingKeys,
    invalidKeys,
  };
}

export function isVariantLegacy(variant, options = []) {
  if (!variant) return false;
  if (variant.is_deleted) return true;
  if (options.length === 0) return false;
  return !getVariantCompleteness(variant, options).complete;
}

export function isVariantReadOnly(variant, options = []) {
  if (!variant) return true;
  return isVariantLegacy(variant, options) || Boolean(variant.is_deleted);
}

export function partitionVariants(variants, options = []) {
  const editable = [];
  const legacy = [];
  (variants || []).forEach((variant) => {
    if (isVariantReadOnly(variant, options)) legacy.push(variant);
    else editable.push(variant);
  });
  return { editable, legacy };
}

export function normalizeProductType(row) {
  if (!row) return null;
  return {
    id: row.id ?? row.ID ?? '',
    name: row.name ?? row.Name ?? '',
    display_order: row.display_order ?? row.DisplayOrder ?? 0,
  };
}

export function normalizeCatalogOption(row) {
  if (!row) return null;
  return {
    id: row.id ?? row.ID ?? '',
    key: row.key ?? row.Key ?? '',
    name: row.name ?? row.Name ?? '',
    display_order: row.display_order ?? row.DisplayOrder ?? 0,
    values: Array.isArray(row.values ?? row.Values) ? [...(row.values ?? row.Values)] : [],
  };
}

function parseMetadataValue(value) {
  if (value == null) return value;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
  return value;
}

export function normalizeCatalogMetadata(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const out = {};
  Object.entries(raw).forEach(([key, value]) => {
    out[key] = parseMetadataValue(value);
  });
  return out;
}

export function normalizeCatalogVariant(row) {
  if (!row) return null;
  const metadata = normalizeCatalogMetadata(row.metadata ?? row.Metadata ?? {});
  let cost = Number(row.cost ?? row.Cost ?? 0);
  if (!cost && metadata.cost != null) {
    cost = Number(parseMetadataValue(metadata.cost)) || 0;
  }
  return {
    id: row.id ?? row.ID ?? '',
    code: row.code ?? row.Code ?? '',
    display_order: row.display_order ?? row.DisplayOrder ?? 0,
    cost,
    price: Number(row.price ?? row.Price ?? 0),
    price_type: row.price_type ?? row.PriceType ?? 'FIXED',
    stock: Number(row.stock ?? row.Stock ?? 0),
    stock_unit: row.stock_unit ?? row.StockUnit ?? '',
    is_made_to_order: Boolean(row.is_made_to_order ?? row.IsMadeToOrder),
    manufacturing_days: Number(row.manufacturing_days ?? row.ManufacturingDays ?? 0),
    status: row.status ?? row.Status ?? 'ACTIVE',
    is_default: Boolean(row.is_default ?? row.IsDefault),
    is_deleted: Boolean(row.is_deleted ?? row.IsDeleted),
    attributes: row.attributes ?? row.Attributes ?? {},
    metadata,
  };
}

export function mergeVariantAttributes(current, patch, options = []) {
  const merged = {
    ...(current?.attributes || {}),
    ...(patch?.attributes || {}),
  };
  for (const opt of options || []) {
    if (!opt?.key) continue;
    const byKey = merged[opt.key];
    const byName = opt.name ? merged[opt.name] : undefined;
    if (!byKey && byName) merged[opt.key] = byName;
  }
  return merged;
}

export function buildSkuUpdatePayload(current, patch = {}, options = []) {
  if (!current) return null;
  const cost = patch.cost !== undefined ? patch.cost : (current.cost ?? 0);
  return {
    code: patch.code ?? current.code,
    display_order: patch.display_order ?? current.display_order,
    price: patch.price ?? current.price,
    price_type: patch.price_type ?? current.price_type,
    stock: patch.stock ?? current.stock,
    stock_unit: current.stock_unit,
    is_made_to_order: patch.is_made_to_order ?? current.is_made_to_order,
    manufacturing_days: patch.manufacturing_days ?? current.manufacturing_days,
    status: patch.status ?? current.status,
    attributes: mergeVariantAttributes(current, patch, options),
    metadata: {
      ...(current.metadata || {}),
      ...(patch.metadata || {}),
      cost,
    },
  };
}

export function buildVariantSimulatorDescription(productName, variant, options) {
  const title = buildVariantTitle(variant, options);
  const name = String(productName || '').trim();
  if (name && title) return `${name} — ${title}`;
  return name || title || '';
}

export function buildSkuPriceSimulatorContext({
  productName,
  variant,
  options,
  defaultVariableCosts = [],
  onApplySimulatedPrice,
  readOnly = false,
}) {
  const effectiveSavedSimulation = getResolvedSavedSimulation(
    variant?.metadata,
    defaultVariableCosts,
  );

  if (!effectiveSavedSimulation && (readOnly || !onApplySimulatedPrice)) {
    return null;
  }

  const simulationQuantity = Math.max(
    1,
    parseInt(effectiveSavedSimulation?.input?.product?.quantity, 10) ||
      parseInt(effectiveSavedSimulation?.result?.detail?.quantity, 10) ||
      1,
  );

  return {
    description: buildVariantSimulatorDescription(productName, variant, options),
    quantity: simulationQuantity,
    productName: productName || '',
    skuCode: variant?.code || '',
    skuAttributes: variant?.attributes || null,
    savedSimulation: effectiveSavedSimulation,
    defaultVariableCosts: Array.isArray(defaultVariableCosts) ? defaultVariableCosts : [],
    attachHistoryAssets: false,
    initialSellingPriceUnit: Number(variant?.price) > 0 ? Number(variant.price) : null,
    onApplySimulatedPrice: readOnly ? undefined : onApplySimulatedPrice,
  };
}

export function normalizeCustomizationField(row) {
  if (!row) return null;
  return {
    id: row.id ?? row.ID ?? '',
    key: row.key ?? row.Key ?? '',
    name: row.name ?? row.Name ?? '',
    is_required: Boolean(row.is_required ?? row.IsRequired),
    customizable_type: row.customizable_type ?? row.CustomizableType ?? null,
    allow_other: Boolean(row.allow_other ?? row.AllowOther),
    display_order: row.display_order ?? row.DisplayOrder ?? 0,
  };
}

export function normalizeCatalog(data) {
  if (!data) return null;
  const options = (data.options ?? data.Options ?? [])
    .map(normalizeCatalogOption)
    .filter(Boolean)
    .sort((a, b) => a.display_order - b.display_order);
  const variants = (data.variants ?? data.Variants ?? [])
    .map(normalizeCatalogVariant)
    .filter(Boolean)
    .sort((a, b) => a.display_order - b.display_order);
  const customizationFields = (data.customization_fields ?? data.CustomizationFields ?? [])
    .map(normalizeCustomizationField)
    .filter(Boolean)
    .sort((a, b) => a.display_order - b.display_order);
  const legacyPreview = (data.legacy_preview ?? data.LegacyPreview ?? [])
    .map(normalizeCatalogVariant)
    .filter(Boolean);
  const legacyCountRaw = data.legacy_count ?? data.LegacyCount;
  const legacyCount =
    typeof legacyCountRaw === 'number' && legacyCountRaw >= 0
      ? legacyCountRaw
      : legacyPreview.length;

  return {
    id: data.id ?? data.ID ?? '',
    code: data.code ?? data.Code ?? '',
    name: data.name ?? data.Name ?? '',
    description: data.description ?? data.Description ?? '',
    status: data.status ?? data.Status ?? 'ACTIVE',
    type: data.type ?? data.Type ?? 'PRODUCT',
    metadata: normalizeCatalogMetadata(data.metadata ?? data.Metadata ?? {}),
    type_ids: Array.isArray(data.type_ids ?? data.TypeIDs) ? [...(data.type_ids ?? data.TypeIDs)] : [],
    categories: (data.categories ?? data.Categories ?? []).map((row) => ({
      category_id: row.category_id ?? row.categoryId ?? row.CategoryID ?? '',
      name: row.name ?? row.Name ?? '',
      slug: row.slug ?? row.Slug ?? '',
      is_main: Boolean(row.is_main ?? row.isMain ?? row.IsMain),
      display_order: row.display_order ?? row.displayOrder ?? row.DisplayOrder ?? 0,
    })),
    attribute_values: (data.attribute_values ?? data.AttributeValues ?? []).map((row) => ({
      attribute_specification_id:
        row.attribute_specification_id ?? row.attributeSpecificationId ?? row.AttributeSpecificationID ?? '',
      value: row.value ?? row.Value ?? '',
    })),
    options,
    customization_fields: customizationFields,
    variants,
    legacy_count: legacyCount,
    legacy_preview: legacyPreview,
  };
}

export function cartesian(arrays) {
  if (!arrays.length) return [[]];
  return arrays.reduce(
    (acc, curr) => acc.flatMap((a) => curr.map((v) => [...a, v])),
    [[]],
  );
}

export function countVariantsFromOptions(options) {
  const withValues = (options || []).filter((o) => o.values && o.values.length > 0);
  if (!withValues.length) return { count: 0, parts: [] };
  const parts = withValues.map((o) => o.values.length);
  const count = parts.reduce((a, b) => a * b, 1);
  return { count, parts };
}

export function buildVariantTitle(variant, options) {
  if (!variant) return '';
  const attrs = variant.attributes || {};
  if (options && options.length > 0) {
    const parts = options
      .map((opt) => attrs[opt.key] ?? attrs[opt.name] ?? '')
      .filter(Boolean);
    if (parts.length) return parts.join(' / ');
  }
  const values = Object.values(attrs).filter(Boolean);
  if (values.length) return values.join(' / ');
  return variant.code || variant.id || '';
}

export function getVariantGroupValue(variant, options, groupByIndex) {
  if (!variant || !options || !options.length) return '';
  const opt = options[groupByIndex] ?? options[0];
  if (!opt) return '';
  return variant.attributes?.[opt.key] ?? variant.attributes?.[opt.name] ?? '';
}

export function filterVariants(variants, options, { searchQuery = '', filters = {} } = {}) {
  const q = String(searchQuery || '').trim().toLowerCase();
  return (variants || []).filter((variant) => {
    const title = buildVariantTitle(variant, options).toLowerCase();
    if (q && !title.includes(q) && !(variant.code || '').toLowerCase().includes(q)) {
      return false;
    }
    for (const opt of options || []) {
      const filterVal = filters[opt.key] ?? filters[opt.name];
      if (!filterVal) continue;
      const part = variant.attributes?.[opt.key] ?? variant.attributes?.[opt.name];
      if (part !== filterVal) return false;
    }
    return true;
  });
}

export function groupVariantsByOption(variants, options, groupByIndex) {
  const grouped = new Map();
  const groupOpt = options?.[groupByIndex] ?? options?.[0];

  (variants || []).forEach((variant) => {
    const gv = getVariantGroupValue(variant, options, groupByIndex);
    if (!grouped.has(gv)) grouped.set(gv, []);
    grouped.get(gv).push(variant);
  });

  const order = groupOpt
    ? groupOpt.values.filter((v) => grouped.has(v))
    : [...grouped.keys()].filter(Boolean);

  return { grouped, order, groupOpt };
}

export function computeValueRenames(initialOptions = [], draftOptions = []) {
  const renames = [];
  const initialByKey = new Map();
  (initialOptions || []).forEach((opt) => {
    const key = String(opt.key ?? '').trim();
    if (key) initialByKey.set(key, opt);
  });
  (draftOptions || []).forEach((opt) => {
    const key = String(opt.key ?? '').trim();
    if (!key || !initialByKey.has(key)) return;
    const initial = initialByKey.get(key);
    const oldValues = (initial.values || []).map((v) => String(v).trim()).filter(Boolean);
    const newValues = (opt.values || []).map((v) => String(v).trim()).filter(Boolean);
    if (oldValues.length === newValues.length) {
      oldValues.forEach((from, index) => {
        const to = newValues[index];
        if (from && to && from !== to) {
          renames.push({ key, from, to });
        }
      });
      return;
    }
    const removed = oldValues.filter((v) => !newValues.includes(v));
    const added = newValues.filter((v) => !oldValues.includes(v));
    if (removed.length === 1 && added.length === 1) {
      renames.push({ key, from: removed[0], to: added[0] });
    }
  });
  return renames;
}

export function optionsToSyncPayload(options, defaultValuesForExisting = {}, valueRenames = []) {
  const payload = {
    options: (options || []).map((opt) => ({
      name: opt.name,
      key: opt.key || undefined,
      values: [...(opt.values || [])],
    })),
  };
  const defaults = defaultValuesForExisting || {};
  if (Object.keys(defaults).length > 0) {
    payload.default_values_for_existing = defaults;
  }
  const renames = (valueRenames || []).filter(
    (r) => r?.key && r?.from && r?.to && String(r.from).trim() !== String(r.to).trim(),
  );
  if (renames.length > 0) {
    payload.value_renames = renames.map((r) => ({
      key: String(r.key).trim(),
      from: String(r.from).trim(),
      to: String(r.to).trim(),
    }));
  }
  return payload;
}

export function cloneOptionsForEditor(options) {
  return (options || []).map((opt) => ({
    id: opt.id,
    key: opt.key,
    name: opt.name,
    values: [...(opt.values || [])],
  }));
}

export function formatListSkuMoneyRange(product, fieldPrefix) {
  const skuCount = product?.sku_count ?? product?.skuCount ?? 0;
  if (!skuCount) return '—';
  const min =
    Number(product?.[`${fieldPrefix}_min`] ?? product?.[`${fieldPrefix}Min`]) || 0;
  const max =
    Number(product?.[`${fieldPrefix}_max`] ?? product?.[`${fieldPrefix}Max`]) || 0;
  if (min === 0 && max === 0) return '';
  if (min === max) {
    return min === 0 ? '' : formatCurrency(min) || '';
  }
  if (min === 0) {
    return formatCurrency(max) || '';
  }
  const minText = formatCurrency(min);
  const maxText = formatCurrency(max);
  return `${minText} ~ ${maxText}`;
}
